import React, { useState, useEffect, useRef } from 'react';
import './BountySelectScreen.css';

// Audio for selection
class SelectAudio {
  constructor() {
    this.ctx = null;
  }

  init() {
    if (this.ctx) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
  }

  select() {
    this.init();
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(600, this.ctx.currentTime);
    osc.frequency.setValueAtTime(800, this.ctx.currentTime + 0.05);
    gain.gain.setValueAtTime(0.15, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.1);
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.1);
  }

  deselect() {
    this.init();
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(400, this.ctx.currentTime);
    osc.frequency.setValueAtTime(300, this.ctx.currentTime + 0.05);
    gain.gain.setValueAtTime(0.15, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.1);
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.1);
  }

  confirm() {
    this.init();
    [523, 659, 784].forEach((f, i) => {
      setTimeout(() => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = f;
        gain.gain.setValueAtTime(0.15, this.ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.15);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start();
        osc.stop(this.ctx.currentTime + 0.15);
      }, i * 100);
    });
  }

  error() {
    this.init();
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.value = 150;
    gain.gain.setValueAtTime(0.2, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.2);
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.2);
  }
}

const audio = new SelectAudio();

// Difficulty labels based on OWASP rank
const getDifficultyLabel = (rank) => {
  if (rank >= 9) return { label: 'ROOKIE', color: '#7CFC00' };
  if (rank >= 7) return { label: 'TRAINED', color: '#ADFF2F' };
  if (rank >= 5) return { label: 'VETERAN', color: '#FFD700' };
  if (rank >= 3) return { label: 'EXPERT', color: '#FF8C00' };
  return { label: 'NIGHTMARE', color: '#FF4444' };
};

const getDifficultyInfo = (rank) => {
  if (rank >= 8) return { rows: 2, speed: 'Slow' };
  if (rank >= 5) return { rows: 3, speed: 'Medium' };
  if (rank >= 2) return { rows: 4, speed: 'Fast' };
  return { rows: 5, speed: 'Extreme' };
};

const BountySelectScreen = ({ vulns, mitigatedBounties, onAccept, onBack }) => {
  const [selected, setSelected] = useState([]);
  const [showRefactorDialog, setShowRefactorDialog] = useState(null);
  const maxBounties = 5;

  const handleSelect = (vuln) => {
    const isSelected = selected.find(s => s.id === vuln.id);
    const isMitigated = mitigatedBounties.includes(vuln.id);

    if (isSelected) {
      // Deselect
      audio.deselect();
      setSelected(selected.filter(s => s.id !== vuln.id));
    } else if (isMitigated) {
      // Ask about refactor
      audio.select();
      setShowRefactorDialog(vuln);
    } else {
      // Select new
      if (selected.length >= maxBounties) {
        audio.error();
        return;
      }
      audio.select();
      setSelected([...selected, vuln]);
    }
  };

  const handleRefactorConfirm = () => {
    if (selected.length < maxBounties) {
      setSelected([...selected, showRefactorDialog]);
    }
    setShowRefactorDialog(null);
  };

  const handleAccept = () => {
    if (selected.length === 0) {
      audio.error();
      return;
    }
    audio.confirm();
    // Sort by rank descending (easiest first: A10 -> A01)
    const sorted = [...selected].sort((a, b) => b.rank - a.rank);
    onAccept(sorted);
  };

  const dangerBar = (level) => {
    const filled = level;
    const empty = 10 - level;
    return '█'.repeat(filled) + '░'.repeat(empty);
  };

  return (
    <div className="bounty-select-screen">
      <div className="select-header">
        <h1>BOUNTY BOARD</h1>
        <p className="select-subtitle">Select up to {maxBounties} targets for your contract</p>
      </div>

      <div className="select-content">
        <div className="bounty-grid">
          {vulns.map(vuln => {
            const isSelected = selected.find(s => s.id === vuln.id);
            const isMitigated = mitigatedBounties.includes(vuln.id);
            const difficulty = getDifficultyLabel(vuln.rank);
            const info = getDifficultyInfo(vuln.rank);
            const selectionOrder = selected.findIndex(s => s.id === vuln.id) + 1;

            return (
              <div
                key={vuln.id}
                className={`bounty-card ${isSelected ? 'selected' : ''} ${isMitigated ? 'mitigated' : ''}`}
                onClick={() => handleSelect(vuln)}
              >
                {isMitigated && !isSelected && (
                  <div className="mitigated-stamp">MITIGATED</div>
                )}
                {isSelected && (
                  <div className="selection-badge">{selectionOrder}</div>
                )}
                <div className="card-header">
                  <span className="card-id">{vuln.id}</span>
                  <span className="card-difficulty" style={{ color: difficulty.color }}>
                    {difficulty.label}
                  </span>
                </div>
                <h3 className="card-name">{vuln.shortName}</h3>
                <div className="card-details">
                  <div className="card-row">
                    <span className="card-label">BOUNTY:</span>
                    <span className="card-bounty">{vuln.bounty.toLocaleString()}</span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">DANGER:</span>
                    <span className="card-danger" style={{ color: vuln.color }}>
                      {dangerBar(vuln.dangerLevel)}
                    </span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">HOSTILES:</span>
                    <span className="card-info">{info.rows} rows</span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">SPEED:</span>
                    <span className="card-info">{info.speed}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="contract-panel">
          <h2>CONTRACT</h2>
          <div className="contract-list">
            {selected.length === 0 ? (
              <p className="contract-empty">No targets selected</p>
            ) : (
              selected.map((vuln, idx) => (
                <div key={vuln.id} className="contract-item">
                  <span className="contract-num">{idx + 1}.</span>
                  <span className="contract-id">{vuln.id}</span>
                  <span className="contract-name">{vuln.shortName}</span>
                  <span className="contract-bounty">+{vuln.bounty}</span>
                </div>
              ))
            )}
          </div>
          <div className="contract-total">
            <span>TOTAL BOUNTY:</span>
            <span className="total-value">
              {selected.reduce((sum, v) => sum + v.bounty, 0).toLocaleString()}
            </span>
          </div>
          <div className="contract-actions">
            <button className="btn back-btn" onClick={onBack}>
              [ BACK ]
            </button>
            <button
              className={`btn accept-btn ${selected.length === 0 ? 'disabled' : ''}`}
              onClick={handleAccept}
            >
              [ ACCEPT ]
            </button>
          </div>
        </div>
      </div>

      {/* Refactor Dialog */}
      {showRefactorDialog && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h3>REFACTOR MISSION?</h3>
            <p>
              <span className="dialog-id">{showRefactorDialog.id}: {showRefactorDialog.shortName}</span>
              <br />
              has already been mitigated.
            </p>
            <p>Do you want to refactor this vulnerability?</p>
            <div className="dialog-buttons">
              <button className="btn" onClick={() => setShowRefactorDialog(null)}>
                [ CANCEL ]
              </button>
              <button className="btn accept-btn" onClick={handleRefactorConfirm}>
                [ REFACTOR ]
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BountySelectScreen;
